import type { Blog } from '@eliadmin/core/src/api/blogs/blogs-types';

import Link from '@eliadmin/core/src/components/link';
import { buttonClassName } from '@eliadmin/core/src/components/svetech/ui/button';
import { CmsImage } from '@eliadmin/core/src/components/svetech/ui/cms-image';
import { Icon } from '@eliadmin/core/src/components/svetech/ui/icon';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { getBlogExcerpt } from '@eliadmin/core/src/utils/blog-utils';
import { formatContentDate } from '@eliadmin/core/src/utils/date-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';

/** Card grid of the blog lists and „Podobné články“: one column, two from `md`, three from `lg`. */
export const BLOG_GRID = 'grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-6 lg:grid-cols-3';

const SIZES = '(min-width: 1440px) 427px, (min-width: 1024px) 31vw, (min-width: 768px) 46vw, 100vw';

/**
 * Article card (Key design „Karta“): the 3:2 photo takes the card's corners, date and categories
 * as muted meta, card title, excerpt and the „Čítať viac“ text button. The title link covers the
 * whole card; the button is its visual cue only, so each card is one tab stop.
 */
export function BlogCard({
  blog,
  locale,
  moreLabel,
  headingAs = 'h2',
}: {
  blog: Blog;
  locale: string;
  /** „Čítať viac“ (t('text1360')) — the card has no translation hook, it renders on both sides. */
  moreLabel: string;
  headingAs?: 'h2' | 'h3';
}) {
  const Title = headingAs;
  const date = formatContentDate(blog.created_at, locale);
  const categories = (blog.categories ?? []).map(category => category.name).join(', ');
  const excerpt = getBlogExcerpt(blog.content, 200);

  return (
    <article
      className={cn(
        'group relative flex h-full flex-col overflow-hidden rounded-3xl rounded-tr-none border border-[#dfe2db] bg-white md:rounded-[32px] md:rounded-tr-none',
        'transition-colors hover:border-[#c9cdc4] motion-reduce:transition-none',
        'has-[a:focus-visible]:ring-2 has-[a:focus-visible]:ring-[#2c7a25] has-[a:focus-visible]:ring-offset-2',
      )}
    >
      <div className="relative aspect-[3/2] w-full shrink-0 bg-[#f2f3ef]">
        <CmsImage image={blog.image} decorative sizes={SIZES} />
      </div>
      <div className="flex flex-1 flex-col gap-2 p-4 md:gap-3 md:p-6">
        {date || categories ? (
          <p className="svt-body-s text-[#6b7681]">
            {date ? <time dateTime={blog.created_at}>{date}</time> : null}
            {date && categories ? <span aria-hidden="true"> · </span> : null}
            {categories}
          </p>
        ) : null}
        <Title className="svt-title-card break-words text-[#14171a] transition-colors group-hover:text-[#2c7a25] motion-reduce:transition-none">
          <Link
            href={ROUTES.BLOG_DETAIL({ slug: blog.slug }) ?? '#'}
            prefetch={false}
            className="after:absolute after:inset-0 focus-visible:outline-none"
          >
            {blog.name}
          </Link>
        </Title>
        {excerpt ? <p className="svt-body line-clamp-3 text-[#4a5560]">{excerpt}</p> : null}
        <span
          aria-hidden="true"
          className={buttonClassName(
            'text',
            'mt-auto h-auto self-start px-0 pt-2 group-hover:text-[#2c7a25] md:h-auto',
          )}
        >
          <span>{moreLabel}</span>
          <Icon name="arrow-right" className="size-6" />
        </span>
      </div>
    </article>
  );
}
